import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Box, ClickAwayListener, InputBase, Popper, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import { defaultAllSelection } from "services/constants/selections";
import { matchesAccessContext } from "services/utils/companyAccess";
import {
  APP_BAR_HEIGHT,
  PANEL_BACKDROP_COLOR,
  SIDEBAR_PANEL_WIDTH_VAR,
  SIDEBAR_RAIL_WIDTH,
  designColors,
  designFontFamily,
  designRadii,
  designWeights,
} from "services/constants/designTokens";
import {
  QUICK_ACTIONS_BEFORE_TYPING,
  buildRegistry,
  findEntryForLocation,
  searchableProducts,
} from "./searchRegistry";
import {
  GROUP_LIMIT,
  RECENT_SHOWN,
  SEE_ALL_LIMIT,
  addRecent,
  getRecent,
  loadProductAccess,
  search,
} from "./searchApi";

// Top-bar search (Cauver Global Search spec v1.0): takes the user to a
// page, a Payroll setting, a quick action or an employee. It does not
// filter tables; each table keeps its own search.

const MIN_QUERY = 2;
const DEBOUNCE_MS = 200;
const LISTBOX_ID = "global-search-listbox";
const optionId = (index) => `global-search-option-${index}`;
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

const GROUPS = [
  { key: "pages", label: "Pages" },
  { key: "settings", label: "Settings" },
  { key: "actions", label: "Quick actions" },
  { key: "employees", label: "Employees" },
];

const TYPE_ICON = {
  page: DescriptionOutlined,
  setting: SettingsOutlined,
  action: AddRounded,
};

const isTypingTarget = (target) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

// Text with the matched letters bold and highlighted (not colour alone).
const Highlighted = ({ text, ranges }) => {
  if (!ranges?.length) return text;
  const parts = [];
  let cursor = 0;
  ranges.forEach(([start, end]) => {
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <Box
        key={start}
        component="mark"
        sx={{ backgroundColor: "#FEF08A", color: "inherit", fontWeight: designWeights.heading, borderRadius: "2px", px: "1px" }}
      >
        {text.slice(start, end)}
      </Box>,
    );
    cursor = end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

const Kbd = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: designFontFamily,
      fontSize: 11,
      lineHeight: "16px",
      px: 0.5,
      border: `1px solid ${designColors.slate300}`,
      borderRadius: "4px",
      color: designColors.slate600,
      backgroundColor: designColors.white,
    }}
  >
    {children}
  </Box>
);

export const GlobalSearch = ({ routes, sidebarOpen, onOpen }) => {
  const history = useHistory();
  const location = useLocation();
  const { domain, userDetails, topbarCompanyId, companyList, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const userId = userDetails?.id;
  const companies = useMemo(() => {
    if (Array.isArray(companyList)) return companyList;
    try {
      return JSON.parse(companyList || "[]");
    } catch {
      return [];
    }
  }, [companyList]);

  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [seeAll, setSeeAll] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentVersion, setRecentVersion] = useState(0);

  // ---- Permissions: each product's payload, loaded when search opens ----
  const products = useMemo(() => searchableProducts(routes), [routes]);
  const productNames = products.map((product) => product.name).join("|");
  const [accessState, setAccessState] = useState({ companyId: null, byProduct: {} });
  const accessByProduct = useMemo(() => {
    const byProduct =
      String(accessState.companyId) === String(topbarCompanyId) ? { ...accessState.byProduct } : {};
    // The product on screen is already loaded by the sidebar.
    const current = userScreenAccess?.product_name;
    if (current && matchesAccessContext(userScreenAccess, topbarCompanyId, current) && !byProduct[current]) {
      byProduct[current] = userScreenAccess;
    }
    return byProduct;
  }, [accessState, topbarCompanyId, userScreenAccess]);

  useEffect(() => {
    if (!open || !domain || !userId || !topbarCompanyId) return undefined;
    let cancelled = false;
    productNames.split("|").filter(Boolean).forEach((productName) => {
      loadProductAccess({
        domain,
        userId,
        userType: userDetails?.type,
        companyId: topbarCompanyId,
        companies,
        productName,
      }).then((access) => {
        if (cancelled || !access) return;
        setAccessState((prev) => ({
          companyId: topbarCompanyId,
          byProduct: {
            ...(String(prev.companyId) === String(topbarCompanyId) ? prev.byProduct : {}),
            [productName]: access,
          },
        }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [open, domain, userId, userDetails?.type, topbarCompanyId, companies, productNames]);

  const context = useMemo(() => ({ domain, companyId: topbarCompanyId }), [domain, topbarCompanyId]);
  const registry = useMemo(
    () => buildRegistry(routes, accessByProduct, context),
    [routes, accessByProduct, context],
  );
  const accessLoading = products.some((product) => !accessByProduct[product.name]);

  // Employees: only for users who can open People > Employee, and only
  // for a single company (the company selector's).
  const companyValid =
    Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== Number(defaultAllSelection);
  const canSeeEmployees =
    companyValid &&
    Boolean(accessByProduct.People?.module_details?.some((module) => module.module_name === "Employee"));
  const employeeSource = useMemo(
    () => (canSeeEmployees ? { domain, companyId: topbarCompanyId } : null),
    [canSeeEmployees, domain, topbarCompanyId],
  );

  // ---- Recent: every registry page opened (from search or the sidebar) ----
  const allPagesRegistry = useMemo(
    () => buildRegistry(routes, {}, context, { ignoreAccess: true }),
    [routes, context],
  );
  useEffect(() => {
    const entry = findEntryForLocation(allPagesRegistry, location.pathname, location.search);
    if (entry) {
      addRecent({ domain, userId }, entry);
      setRecentVersion((n) => n + 1);
    }
  }, [allPagesRegistry, location.pathname, location.search, domain, userId]);

  const recentEntries = useMemo(() => {
    const byId = new Map([...registry.pages, ...registry.settings].map((entry) => [entry.id, entry]));
    return getRecent({ domain, userId })
      .map((item) => byId.get(item.id))
      .filter(Boolean)
      .slice(0, RECENT_SHOWN);
    // recentVersion: re-read after a page is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registry, domain, userId, recentVersion]);
  const recentIds = useMemo(() => recentEntries.map((entry) => entry.id), [recentEntries]);

  const currentModule = useSelector((state) => state.tokenReducer.currentLocation);
  const quickActions = useMemo(
    () => registry.actions.filter((action) => action.module === currentModule).slice(0, QUICK_ACTIONS_BEFORE_TYPING),
    [registry, currentModule],
  );

  // ---- Searching: 2+ characters, 200 ms after the last key, latest wins ----
  const trimmed = query.trim();
  const typing = trimmed.length >= MIN_QUERY;
  useEffect(() => {
    if (!open || !typing) {
      setResults(null);
      setSearching(false);
      return undefined;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      search({
        query: trimmed,
        registry,
        recentIds,
        employeeSource,
        limit: seeAll ? SEE_ALL_LIMIT : GROUP_LIMIT,
        signal: controller.signal,
      })
        .then((found) => {
          if (controller.signal.aborted) return;
          setResults(found);
          setSearching(false);
        })
        .catch((error) => {
          if (error?.name !== "AbortError") setSearching(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, typing, trimmed, registry, recentIds, employeeSource, seeAll]);

  // The rows on screen, in order, for the keyboard.
  const sections = useMemo(() => {
    if (!typing) {
      return [
        { key: "recent", label: "Recent", items: recentEntries },
        { key: "quick", label: "Quick actions", items: quickActions },
      ].filter((section) => section.items.length > 0);
    }
    if (!results) return [];
    return GROUPS.map((group) => ({ ...group, items: results[group.key] || [] })).filter(
      (section) => section.items.length > 0,
    );
  }, [typing, results, recentEntries, quickActions]);
  const flatItems = useMemo(() => sections.flatMap((section) => section.items), [sections]);
  const hasMore = typing && !seeAll && results && Object.values(results.more || {}).some(Boolean);

  // The first row is pre-selected whenever the list changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [sections]);
  useEffect(() => {
    setSeeAll(false);
  }, [trimmed]);
  useEffect(() => {
    if (!open) return;
    document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults(null);
    setSeeAll(false);
  }, []);

  const openEntry = useCallback(
    (entry, newTab = false) => {
      if (!entry) return;
      if (newTab) {
        window.open(entry.route, "_blank", "noopener");
        return;
      }
      close();
      inputRef.current?.blur();
      if (onOpen) onOpen(entry);
      else history.push(entry.route);
    },
    [close, history, onOpen],
  );

  // Ctrl/Cmd + K anywhere, or "/" when not typing in a field, opens it.
  useEffect(() => {
    const onKeyDown = (event) => {
      const isShortcut = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
      const isSlash = event.key === "/" && !event.ctrlKey && !event.metaKey && !event.altKey && !isTypingTarget(event.target);
      if (!isShortcut && !isSlash) return;
      event.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // A page change (from anywhere) closes it.
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  const handleKeyDown = (event) => {
    const count = flatItems.length;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      if (count) setActiveIndex((index) => (index + 1) % count);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (count) setActiveIndex((index) => (index - 1 + count) % count);
    } else if (event.key === "Enter") {
      event.preventDefault();
      openEntry(flatItems[activeIndex], event.ctrlKey || event.metaKey);
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
      inputRef.current?.blur();
    } else if (event.key === "Tab") {
      close();
    }
  };

  const showPanel = open;
  const panelWidth = Math.max(anchorRef.current?.offsetWidth || 0, 480);
  let rowIndex = -1;

  const renderRow = (entry) => {
    rowIndex += 1;
    const index = rowIndex;
    const active = index === activeIndex;
    const Icon = entry.type === "employee" ? null : entry.row?.icon || TYPE_ICON[entry.type] || DescriptionOutlined;
    return (
      <Box
        key={`${entry.id}-${index}`}
        id={optionId(index)}
        role="option"
        aria-selected={active}
        onMouseMove={() => !active && setActiveIndex(index)}
        // Keep focus in the input so Esc and the arrows keep working.
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => openEntry(entry, event.ctrlKey || event.metaKey)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.25,
          py: 0.75,
          mx: 0.75,
          borderRadius: designRadii.control,
          cursor: "pointer",
          backgroundColor: active ? designColors.tealTint : "transparent",
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: entry.type === "employee" ? "50%" : "8px",
            backgroundColor: entry.type === "employee" ? designColors.slate200 : designColors.slateBg,
            border: entry.type === "employee" ? "none" : `1px solid ${designColors.slate200}`,
            color: designColors.slate600,
            fontSize: 11,
            fontWeight: designWeights.heading,
            fontFamily: designFontFamily,
          }}
        >
          {Icon ? <Icon sx={{ fontSize: 16 }} /> : entry.initials}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontFamily: designFontFamily, fontSize: 13, fontWeight: designWeights.semibold, color: designColors.slate900 }}>
            <Highlighted text={entry.title} ranges={entry.highlight} />
          </Typography>
          {entry.path ? (
            <Typography noWrap sx={{ fontFamily: designFontFamily, fontSize: 11.5, color: designColors.slate600 }}>
              <Highlighted text={entry.path} ranges={entry.pathHighlight} />
            </Typography>
          ) : null}
        </Box>
        {active ? <Kbd>Enter</Kbd> : null}
      </Box>
    );
  };

  const renderBody = () => {
    if (typing && sections.length === 0) {
      if (searching || !results || accessLoading) {
        return (
          <Typography sx={{ px: 2, py: 2.5, fontFamily: designFontFamily, fontSize: 13, color: designColors.slate600, textAlign: "center" }}>
            Searching…
          </Typography>
        );
      }
      return (
        <Box sx={{ px: 3, py: 3, textAlign: "center" }}>
          <SearchIcon sx={{ fontSize: 22, color: designColors.slate400 }} />
          <Typography sx={{ mt: 0.5, fontFamily: designFontFamily, fontSize: 13.5, fontWeight: designWeights.heading, color: designColors.slate900 }}>
            No results for “{trimmed}”
          </Typography>
          <Typography sx={{ mt: 0.5, fontFamily: designFontFamily, fontSize: 12, color: designColors.slate600 }}>
            Check the spelling, or try a shorter word. You only see pages and employees your role can open.
          </Typography>
        </Box>
      );
    }
    if (!typing && sections.length === 0) {
      return (
        <Typography sx={{ px: 2, py: 2.5, fontFamily: designFontFamily, fontSize: 13, color: designColors.slate600, textAlign: "center" }}>
          {accessLoading ? "Loading…" : "Type at least 2 letters to search pages, settings and employees."}
        </Typography>
      );
    }
    return (
      <>
        {sections.map((section) => (
          <Box key={section.key} role="group" aria-label={section.label} sx={{ pb: 0.5 }}>
            <Typography
              sx={{
                px: 2,
                pt: 1.25,
                pb: 0.5,
                fontFamily: designFontFamily,
                fontSize: 10.5,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontWeight: designWeights.heading,
                color: designColors.slate600,
              }}
            >
              {section.label}
            </Typography>
            {section.items.map(renderRow)}
          </Box>
        ))}
        {hasMore ? (
          <Box
            component="button"
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setSeeAll(true)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mx: 2,
              my: 0.75,
              p: 0,
              border: 0,
              background: "none",
              cursor: "pointer",
              fontFamily: designFontFamily,
              fontSize: 12.5,
              fontWeight: designWeights.heading,
              color: designColors.tealDark,
            }}
          >
            See all results for “{trimmed}” <ArrowForwardRounded sx={{ fontSize: 15 }} />
          </Box>
        ) : null}
      </>
    );
  };

  return (
    <ClickAwayListener onClickAway={() => open && close()}>
      <Box sx={{ flex: "1 1 auto", minWidth: 140, maxWidth: 440 }}>
        <Box
          ref={anchorRef}
          onClick={() => {
            setOpen(true);
            inputRef.current?.focus();
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: 36,
            px: 1.25,
            borderRadius: designRadii.control,
            border: `1px solid ${open ? designColors.teal : designColors.borderInput}`,
            boxShadow: open ? `0 0 0 3px ${designColors.tealTint}` : "none",
            backgroundColor: designColors.white,
            cursor: "text",
            transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            "&:hover": { borderColor: open ? designColors.teal : designColors.borderInputHover },
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: designColors.slate400, flexShrink: 0 }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Search Cauver — pages, settings, employees"
            inputProps={{
              role: "combobox",
              "aria-label": "Search Cauver",
              "aria-expanded": showPanel,
              "aria-controls": LISTBOX_ID,
              "aria-autocomplete": "list",
              "aria-activedescendant": showPanel && flatItems.length ? optionId(activeIndex) : undefined,
              autoComplete: "off",
              spellCheck: false,
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: designFontFamily,
              fontSize: 13,
              color: designColors.slate900,
              "& input::placeholder": { color: designColors.slate400, opacity: 1 },
            }}
          />
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.5, flexShrink: 0 }}>
            <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
            <Kbd>K</Kbd>
          </Box>
        </Box>

        {/* The page behind dims slightly; the sidebar and top bar don't. */}
        {showPanel ? (
          <Box
            aria-hidden
            onClick={close}
            sx={{
              position: "fixed",
              top: APP_BAR_HEIGHT,
              right: 0,
              bottom: 0,
              left: sidebarOpen ? `calc(${SIDEBAR_RAIL_WIDTH}px + ${SIDEBAR_PANEL_WIDTH_VAR})` : `${SIDEBAR_RAIL_WIDTH}px`,
              backgroundColor: PANEL_BACKDROP_COLOR,
              zIndex: (theme) => theme.zIndex.appBar - 1,
            }}
          />
        ) : null}

        <Popper
          open={showPanel}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          // The top bar is fixed, so the dropdown is too: scrolling the page
          // keeps it right under the box instead of carrying it away.
          popperOptions={{ strategy: "fixed" }}
          modifiers={[
            { name: "offset", options: { offset: [0, 6] } },
            { name: "flip", enabled: false },
            { name: "preventOverflow", options: { altAxis: false, padding: 12 } },
          ]}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Box
            sx={{
              width: panelWidth,
              maxWidth: "calc(100vw - 24px)",
              backgroundColor: designColors.white,
              border: `1px solid ${designColors.slate200}`,
              borderRadius: "12px",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)",
              overflow: "hidden",
              fontFamily: designFontFamily,
            }}
          >
            <Box
              id={LISTBOX_ID}
              role="listbox"
              aria-label="Search results"
              sx={{ maxHeight: "min(60vh, 520px)", overflowY: "auto", py: 0.5 }}
            >
              {renderBody()}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.75,
                borderTop: `1px solid ${designColors.slate200}`,
                backgroundColor: designColors.slateBg,
                fontSize: 11,
                color: designColors.slate600,
                fontFamily: designFontFamily,
                // Always one line, nothing cut: with this spacing the hints and
                // the note measure about 468px, inside the 480px panel.
                whiteSpace: "nowrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd> move
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <Kbd>Enter</Kbd> open
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <Kbd>Esc</Kbd> close
              </Box>
              <Box sx={{ ml: "auto", pl: 0.5, flexShrink: 0 }}>Only shows what you are allowed to open</Box>
            </Box>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
